import { formatLykCoinValue, formatLykCoins } from '@/lib/lyk-coin'
import { useUser } from '@/src/store/user-store'
import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
} from '@chakra-ui/menu'
import { Avatar, Flex, Text } from '@chakra-ui/react'
import { ChevronDown } from 'lucide-react'
import Image from 'next/image'
import { Icons } from '../icons'
import Link from 'next/link'
import { useLogoutMutation } from '@/src/mutations/auth/logout-mutation'
import { useWalletBalanceQuery } from '@/src/queries/earnings/wallet-balance'
import { getInitials } from './avatar'
import { useState } from 'react'

const getImageUrl = (url: string) => {
  if (url.startsWith('http')) {
    return url
  } else {
    return `https://cdn.lykstage.com/img/${url}`
  }
}

export const UserProfileBtn = () => {
  const { user, logout } = useUser()
  const logoutMutation = useLogoutMutation()
  const walletBalance = useWalletBalanceQuery()
  const lykCoinBalance = formatLykCoins(walletBalance.data?.verified.lykCoin)
  const lykCoinValue = formatLykCoinValue(lykCoinBalance)
  const [isWalletHovered, setIsWalletHovered] = useState(false)
  const displayFirstName = user?.firstName
    ? user.firstName.length > 15
      ? `${user.firstName.slice(0, 15)}...`
      : user.firstName
    : ''
  const defaultWalletLabel = `${lykCoinBalance} LYK Coins`
  const hoverWalletLabel = `US$ ${lykCoinValue}`
  const widestWalletLabel =
    defaultWalletLabel.length >= hoverWalletLabel.length
      ? defaultWalletLabel
      : hoverWalletLabel

  const handleDiwaliContestClear = () => {
    localStorage.removeItem('navratriContestLastClosedDate')
  }

  return (
    <Menu>
      <MenuButton
        onMouseEnter={() => setIsWalletHovered(true)}
        onMouseLeave={() => setIsWalletHovered(false)}
        onFocus={() => setIsWalletHovered(true)}
        onBlur={() => setIsWalletHovered(false)}
      >
        <Flex
          gap={{
            base: 1,
            md: 3,
          }}
          maxW={280}
          minW={{
            base: 'auto',
            md: 150,
          }}
          alignItems="center"
        >
          <Avatar
            getInitials={getInitials}
            w={10}
            h={10}
            bg={user?.themeColor || '#E79051'}
            color="white"
            name={user?.channelName}
            src={getImageUrl(user?.imageUrl || '')}
          />
          <Flex direction="column" hideBelow="md">
            <Text
              fontSize="medium"
              fontWeight="bold"
              noOfLines={1}
              textAlign="start"
            >
              {displayFirstName}
            </Text>
            <Flex
              align="center"
              gap={2}
              mt={1}
              w="fit-content"
              maxW="100%"
              alignSelf="flex-start"
              px={2}
              py={1}
              border="1px solid"
              borderColor={isWalletHovered ? '#EAF9F6' : '#24BEA2'}
              borderRadius="full"
              bg={isWalletHovered ? '#084F41' : '#EAF9F6'}
              color={isWalletHovered ? '#EAF9F6' : '#24BFA3'}
              overflow="hidden"
              transition="all 0.2s ease"
            >
              <Flex
                align="center"
                sx={{
                  '& img': {
                    transition: 'filter 0.2s ease',
                    filter: isWalletHovered ? 'brightness(0) invert(1)' : 'none',
                  },
                }}
              >
                <Image
                  src={'/icons/wallet.svg'}
                  width={20}
                  height={20}
                  alt="wallet icon"
                  priority
                  loading="eager"
                />
              </Flex>
              <Text
                position="relative"
                fontSize={13.7}
                fontWeight={600}
                textAlign="left"
                whiteSpace="nowrap"
              >
                <Text as="span" visibility="hidden">
                  {widestWalletLabel}
                </Text>
                <Text as="span" position="absolute" inset={0}>
                  {isWalletHovered ? hoverWalletLabel : defaultWalletLabel}
                </Text>
              </Text>
            </Flex>
          </Flex>
          <ChevronDown size={20} />
        </Flex>
      </MenuButton>
      <MenuList color="#3E3E3E" fontWeight={500} fontSize={14}>
        <MenuGroup>
          <MenuItem
            icon={<Icons.userCircle color="#787878" size={20} />}
            opacity={0.75}
            _hover={{ bg: '#ECECEC' }}
            px={5}
            py={2}
            as={Link}
            href={'/account'}
          >
            Profile
          </MenuItem>
          <MenuItem
            icon={<Icons.Premium color="#787878" size={20} />}
            opacity={0.75}
            _hover={{ bg: '#ECECEC' }}
            px={5}
            py={2}
            as={Link}
            href={'/account/membership'}
          >
            Account Management
          </MenuItem>
          {/* <MenuItem
            icon={<Icons.play color="#787878" size={20} />}
            opacity={0.75}
            _hover={{ bg: '#ECECEC' }}
            px={5}
            py={2}
            as={Link}
            href={'/c/' + user?.channelShareName}
          >
            My Channel
          </MenuItem>
          <Link href={'/earnings'}>
            <MenuItem
              icon={<Icons.wallet color="#787878" size={20} stroke="#787878" />}
              opacity={0.75}
              _hover={{ bg: '#ECECEC' }}
              px={5}
              py={2}
            >
              My Earnings
            </MenuItem>
          </Link> */}
        </MenuGroup>
        <MenuDivider />
        <MenuGroup>
          {/* <MenuItem
            icon={<Icons.settings color="#787878" size={20} />}
            opacity={0.75}
            _hover={{ bg: '#ECECEC' }}
            px={5}
            py={2}
          >
            Settings
          </MenuItem>*/}
          <Link href={'/faqs'}>
            <MenuItem
              icon={<Icons.help />}
              opacity={0.75}
              _hover={{ bg: '#ECECEC' }}
              px={5}
              py={2}
            >
              Help & FeedBack
            </MenuItem>
          </Link>
          <Link href={'/home'}>
            <MenuItem
              icon={<Icons.logOut color="#787878" size={18} />}
              opacity={0.75}
              _hover={{ bg: '#ECECEC' }}
              px={5}
              py={2}
              onClick={() => {
                logout()
                logoutMutation.mutate()
                handleDiwaliContestClear()
              }}
            >
              Logout
            </MenuItem>
          </Link>
        </MenuGroup>
      </MenuList>
    </Menu>
  )
}
